import React, { useEffect, useMemo, useRef, useState } from 'react';
import useDebounced from '../../hooks/useDebounced';
import InputField from '../Forms/Input/InputField';
import SelectField from '../Forms/Input/SelectField';
import SimpleSearchSelect from '../Forms/Input/SimpleSearchSelect';
import DatePicker from '../Forms/DatePicker/DatePicker';
import clsx from 'clsx';
import SvgIcon from '../SvgIcon/SvgIcon';
import { useReportExport } from '../../hooks/report/useReportExport';
import { IconKey } from '../../types/system/icons';

export type UniversalFilterType =
  | 'text'
  | 'number'
  | 'select'
  | 'searchSelect'
  | 'date'
  | 'dateTime';

export interface UniversalFilterConfig {
  name: string;
  label: string;
  type: UniversalFilterType;
  options?: { id: string | number; name: string }[];
  colSpan?: number;
  iconKey?: IconKey;
  required?: boolean; // 🔥 Nuevo campo opcional
}

interface ReportFilterProps {
  availableReportTypes: {
    id: number;
    name: string;
    code: string;
  }[];
  selectedReportTypeId: number | null;
  onReportTypeChange: (id: number | null) => void;
  onResetFilters?: () => void;
  onGenerateReport?: () => void;
  filtersConfig: UniversalFilterConfig[];
}

const ReportFilter: React.FC<ReportFilterProps> = ({
  availableReportTypes,
  selectedReportTypeId,
  onReportTypeChange,
  onResetFilters,
  onGenerateReport,
  filtersConfig,
}) => {
  const skipNextEffectRef = useRef(false);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const { handlerSetFilters, handlerSetReportCode } = useReportExport();
  const debouncedFilters = useDebounced(filters, 700);

  const filterTypesMap = useMemo(() => {
    return filtersConfig.reduce((acc, curr) => {
      acc[curr.name] = curr.type;
      return acc;
    }, {} as Record<string, UniversalFilterType>);
  }, [filtersConfig]);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    const hasAnyValue = Object.values(debouncedFilters).some(
      (val) => val !== '' && val !== null && val !== undefined
    );
    if (!hasAnyValue) return;

    const parsedFilters: Record<string, any> = { ...debouncedFilters };
    Object.keys(parsedFilters).forEach((key) => {
      const type = filterTypesMap[key];
      const value = parsedFilters[key];

      if ((type === 'number' || type === 'searchSelect') && value !== '') {
        parsedFilters[key] = Number(value);
      }
    });

    handlerSetFilters(parsedFilters); // ✅ Actualiza en Redux
  }, [debouncedFilters, filterTypesMap]);

  const resetFilters = () => {
    skipNextEffectRef.current = true;
    setFilters({});
    handlerSetFilters({}); // 🧠 Limpia también en Redux
    onReportTypeChange(null);
    onResetFilters?.();
  };

  const renderField = (filter: UniversalFilterConfig) => {
    const { name, label, type, options, iconKey = '' } = filter;

    switch (type) {
      case 'text':
      case 'number':
        return (
          <InputField
            key={name}
            label={label}
            name={name}
            type={type}
            value={filters[name] || ''}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, [name]: e.target.value }))
            }
            placeholder={
              type === 'number' ? '0.00' : `Ingrese ${label.toLowerCase()}`
            }
            iconKey={iconKey}
          />
        );
      case 'select':
        return (
          <SelectField
            key={name}
            label={label}
            name={name}
            value={filters[name] || ''}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, [name]: e.target.value }))
            }
            options={(options || []).map((opt) => ({
              label: opt.name,
              value: String(opt.id),
            }))}
            placeholder="Seleccione"
            iconKey={iconKey}
          />
        );
      case 'searchSelect':
        return (
          <SimpleSearchSelect
            key={name}
            label={label}
            name={name}
            options={options || []}
            selectedValue={filters[name] ? parseInt(filters[name]) : null}
            onSelect={(selected) =>
              setFilters((prev) => ({
                ...prev,
                [name]: String(selected.id),
              }))
            }
            searchPlaceholder={`Buscar ${label.toLowerCase()}...`}
            iconKey={iconKey}
          />
        );
      case 'date':
      case 'dateTime':
        return (
          <DatePicker
            key={name}
            label={label}
            name={name}
            value={filters[name] || ''}
            onChange={(date) =>
              setFilters((prev) => ({ ...prev, [name]: date }))
            }
            placeholder="Seleccione una fecha"
            iconKey={iconKey}
            enableTime={type === 'dateTime'}
          />
        );
      default:
        return null;
    }
  };

  const getGridCols = () => {
    const count = filtersConfig.length;
    if (count <= 2) return 'grid-cols-1 sm:grid-cols-2';
    if (count <= 4) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2';
    if (count <= 6) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4';
  };

  return (
    <div className="px-6 pb-6 pt-6">
      {/* Selector de tipo de reporte */}
      <div className="mb-6 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <div className="w-full sm:w-1/2 md:w-1/3">
          <SimpleSearchSelect
            label="Tipo de Reporte"
            name="reportType"
            options={availableReportTypes.map((type) => ({
              id: type.id,
              name: type.name,
            }))}
            selectedValue={selectedReportTypeId}
            onSelect={(selected) => {
              onReportTypeChange(selected ? selected.id : null); // Actualiza ID seleccionado
              setFilters({}); // Limpia filtros del formulario
              handlerSetFilters({}); // Limpia filtros en Redux
              if (selected?.code) {
                handlerSetReportCode(selected.code); // Guarda código en Redux
              }
            }}
            searchPlaceholder="Buscar tipo de reporte..."
            iconKey="document"
          />
        </div>

        <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto sm:justify-start">
          <button
            type="button"
            onClick={() => {
              setFilters({});
              handlerSetFilters({});
            }}
            className="border-gray-300 inline-flex items-center gap-2 rounded border bg-[#F4F6F8] px-5 py-2.5 text-sm font-medium text-[#212B36] hover:bg-[#DFE3E8]"
          >
            <SvgIcon name="continue" />
            Limpiar filtros
          </button>

          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-2 rounded bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white hover:bg-opacity-90"
          >
            <SvgIcon name="cancel" />
            Restablecer todo
          </button>

          {onGenerateReport && (
            <button
              type="button"
              onClick={() => {
                const missingRequired = filtersConfig.filter(
                  (f) => f.required && !filters[f.name]
                );
                if (missingRequired.length > 0) {
                  alert(
                    `Faltan campos requeridos: ${missingRequired
                      .map((f) => f.label)
                      .join(', ')}`
                  );
                  return;
                }

                onGenerateReport();
              }}
              className="inline-flex items-center gap-2 rounded bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-opacity-90"
            >
              <SvgIcon name="download" />
              Generar Reporte
            </button>
          )}
        </div>
      </div>

      {selectedReportTypeId && filtersConfig.length > 0 && (
        <div className={clsx('grid gap-4', getGridCols())}>
          {filtersConfig.map((filter) => (
            <div key={filter.name} className="w-full">
              {renderField(filter)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportFilter;
